import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Dropdown, Modal, Input, message } from "antd";
import { Tag, FolderOpen, Star, ArrowBendUpRight, Printer, Copy, Books, PencilSimple, PenNib, Stack, SquaresFour, Trash, ClipboardText } from "@phosphor-icons/react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectGoldStandardForDocument,
  deleteGoldStandard,
} from "../../slice/CategorizationSlice";
import { copyDocumentsToClipboard, selectClipboardItems } from "../../slice/DocumentClipboardSlice";
import {
  applyDocumentRename,
  deleteDoc,
  fetchMatterDetail,
  fetchMatterDetailList,
} from "../../slice/MatterDetailsSlice";
import { pdfRenameDocument } from "../../service/editPDF";
import { copyDocumentsApi, bulkDeleteMatterDocuments } from "../../service/matter";
import { applyPoaSignature } from "../../service/settings";
import PoaSignatureSelect from "../PoaSignatureSelect/PoaSignatureSelect";
import AttorneySignatureModal from "../AttorneySignature/AttorneySignatureModal";
import {
  fetchMatterCollections,
  createCollection,
  addCollectionDocuments,
  prepareCollection,
} from "../../service/documentCollections";
import CategorizeDocumentModal from "../Categorization/CategorizeDocumentModal";
import MoveDocumentModal from "./MoveDocumentModal";
import SendFaxModal from "./SendFaxModal";
import AddToBriefBankModal from "../BriefBank/AddToBriefBankModal";
import SaveAsCollectionModal from "../TabView/SaveAsCollectionModal";
import CustomModal from "../common/CustomModal/CustomModal";
import {
  copySuccessMessage,
  documentIdForCopy,
  extractCopyApiError,
  formatPasteNote,
  pasteMenuLabel,
} from "../../utils/documentClipboard";
import {
  selectCurrentMatterId,
  collectDocumentsByUniqueIds,
} from "./documentMoveUtils";
import useAuth from "../../hooks/useAuth";

export { selectCurrentMatterId };

const ICON_SIZE = 16;
// Canonical danger fg from src/theme/tokens.js (status.failed.fg) — matches
// FolderContextMenu's Delete styling so the row-level menu reads consistently
// whether you right-click a file or a folder.
const DANGER_FG = "#A61412";

const stripDocKey = (key) => {
  if (typeof key !== "string") return null;
  return key.startsWith("D") ? key.slice(1) : key;
};

const DocumentContextMenu = ({
  documentNode,
  parentFolderName = "",
  onCombineRequest,
  children,
}) => {
  const dispatch = useDispatch();
  const record = (documentNode && documentNode.record) || {};
  const docIdRaw =
    record.id ??
    record.document_id ??
    record.documentId ??
    stripDocKey(record.unique_id) ??
    stripDocKey(documentNode?.key);
  const docKey = docIdRaw != null ? String(docIdRaw) : null;
  const docName =
    record.name ||
    (typeof documentNode?.title === "string" ? documentNode.title : "") ||
    "";
  const inferredCategory =
    record.doc_category || record.category || parentFolderName || "";

  // Gold Standard library additions are Matthew-only (2026-08-05 access
  // control rollout) — hidden from the menu entirely for everyone else,
  // not just disabled, so other users never see the option exists.
  const { hasCapability } = useAuth();
  const canUseGoldStandard = hasCapability("ai.gold_standard");
  const canPlaceAttorneySignature = hasCapability("esign.attorney_signature");

  const goldStandard = useSelector((state) =>
    selectGoldStandardForDocument(state, docKey)
  );
  const matterId = useSelector(selectCurrentMatterId);
  const clipboardItems = useSelector(selectClipboardItems);
  // Multi-selection (ctrl/cmd-click in the tree): when the right-clicked
  // document is part of it, Copy acts on the whole selection.
  const selectedKeys = useSelector(
    (state) => state.matterDetails?.selectedKeys || []
  );
  const rawTree = useSelector(
    (state) => state.matterDetails?.matterDetailsTree || null
  );
  const nodeUniqueId = record.unique_id || documentNode?.key || null;
  const selectionIncludesThis =
    nodeUniqueId != null &&
    selectedKeys.some((key) => String(key) === String(nodeUniqueId));
  const selectionDocCount = selectedKeys.filter((key) =>
    String(key).startsWith("D")
  ).length;
  const bulkCopyActive = selectionIncludesThis && selectionDocCount > 1;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("gold-standard");
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [faxModalOpen, setFaxModalOpen] = useState(false);
  const [briefBankModalOpen, setBriefBankModalOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [pendingName, setPendingName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [poaModalOpen, setPoaModalOpen] = useState(false);
  const [poaName, setPoaName] = useState("");
  const [applyingPoa, setApplyingPoa] = useState(false);
  const [attorneySignatureOpen, setAttorneySignatureOpen] = useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [collectionCandidates, setCollectionCandidates] = useState([]);
  const [matterCollections, setMatterCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [savingCollection, setSavingCollection] = useState(false);
  const listState = useSelector((state) => ({
    currentPage: state.matterDetails?.currentPage,
    sort: state.matterDetails?.sort,
    sortBy: state.matterDetails?.sortBy,
  }));
  const fileType = String(record.file_type || docName.split(".").pop() || "")
    .toLowerCase()
    .replace(".", "");
  const isWordDocument = fileType === "doc" || fileType === "docx";

  const openModal = useCallback(
    (mode) => {
      if (!docKey) return;
      setModalMode(mode);
      setModalOpen(true);
    },
    [docKey]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!goldStandard?.id) return;
    setRemoving(true);
    try {
      await dispatch(deleteGoldStandard(goldStandard.id)).unwrap();
      message.success("Removed from Gold Standard Library");
    } catch {
      message.error("Failed to remove from Gold Standard Library");
    } finally {
      setRemoving(false);
      setRemoveConfirmOpen(false);
    }
  }, [dispatch, goldStandard]);

  const goldStandardItem = useMemo(() => {
    if (!canUseGoldStandard) return null;
    if (goldStandard) {
      return {
        key: "gold-standard",
        label: "In Gold Standard Library",
        icon: <Star size={ICON_SIZE} weight="fill" />,
        disabled: !docKey,
        className: "gold-standard-active",
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setRemoveConfirmOpen(true);
        },
      };
    }
    return {
      key: "gold-standard",
      label: "Add to Gold Standard Library",
      icon: <Tag size={ICON_SIZE} weight="regular" />,
      disabled: !docKey,
      onClick: ({ domEvent }) => {
        domEvent?.stopPropagation?.();
        openModal("gold-standard");
      },
    };
  }, [goldStandard, docKey, openModal, canUseGoldStandard]);

  const openRename = useCallback(() => {
    if (!docKey) return;
    setPendingName(docName);
    setRenameOpen(true);
  }, [docKey, docName]);

  const handleRenameSubmit = useCallback(async () => {
    const name = (pendingName || "").trim();
    if (!name || renaming) return;
    if (name === docName) {
      setRenameOpen(false);
      return;
    }
    setRenaming(true);
    try {
      await pdfRenameDocument(docKey, name);
      // Instant rename everywhere (tree, table, open detail); the refetches
      // below reconcile in the background — waiting on them made renames
      // feel sluggish on big matters.
      dispatch(applyDocumentRename({ documentId: docKey, name }));
      message.success(`Renamed to "${name}".`);
      setRenameOpen(false);
      if (matterId) {
        dispatch(fetchMatterDetail(matterId));
        dispatch(
          fetchMatterDetailList({
            id: matterId,
            page: listState.currentPage || 1,
            sort_direction: listState.sort,
            sort_by: listState.sortBy,
          })
        );
      }
    } catch (err) {
      message.error(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Couldn't rename the document — try again."
      );
    } finally {
      setRenaming(false);
    }
  }, [pendingName, renaming, docName, docKey, matterId, dispatch, listState]);

  const handleCopy = useCallback(() => {
    const copiedId = documentIdForCopy(record) || docKey;
    if (!copiedId) return;
    if (bulkCopyActive) {
      const docKeys = selectedKeys.filter((key) => String(key).startsWith("D"));
      let items = collectDocumentsByUniqueIds(rawTree, docKeys);
      if (items.length === 0) {
        items = [{ id: copiedId, name: docName }];
      }
      dispatch(
        copyDocumentsToClipboard({ items, sourceMatterId: matterId })
      );
      message.success(copySuccessMessage(items));
      return;
    }
    const items = [{ id: copiedId, name: docName }];
    dispatch(
      copyDocumentsToClipboard({
        items,
        sourceMatterId: matterId,
      })
    );
    message.success(copySuccessMessage(items));
  }, [
    dispatch,
    docKey,
    docName,
    matterId,
    bulkCopyActive,
    selectedKeys,
    rawTree,
    record,
  ]);

  // "Combine files" only makes sense for the multi-selection this row is
  // part of (a single file has nothing to combine with) — TreeComponent
  // owns the actual naming/destination modal so the in-flight job survives
  // any tree refresh, not just this row's lifetime.
  const handleCombine = useCallback(() => {
    if (!docKey || !bulkCopyActive || typeof onCombineRequest !== "function") {
      return;
    }
    const docKeys = selectedKeys.filter((key) => String(key).startsWith("D"));
    let items = collectDocumentsByUniqueIds(rawTree, docKeys);
    if (items.length < 2) {
      items = [{ id: docKey, name: docName }];
    }
    onCombineRequest(items);
  }, [docKey, bulkCopyActive, onCombineRequest, selectedKeys, rawTree, docName]);

  // "Add to collection" reuses the same picker Matthew already ships from
  // the file table's selection bar (SaveAsCollectionModal) so a saved
  // collection reads identically no matter which surface created it.
  const handleOpenAddToCollection = useCallback(async () => {
    if (!docKey) return;
    let candidates = [{ id: docKey, name: docName }];
    if (bulkCopyActive) {
      const docKeys = selectedKeys.filter((key) => String(key).startsWith("D"));
      const resolved = collectDocumentsByUniqueIds(rawTree, docKeys);
      if (resolved.length >= 2) candidates = resolved;
    }
    setCollectionCandidates(candidates);
    setCollectionModalOpen(true);
    setLoadingCollections(true);
    try {
      const data = await fetchMatterCollections(matterId);
      setMatterCollections(data?.collections || []);
    } catch (err) {
      // A failed list still leaves "New collection…" usable in the modal —
      // don't block on it.
      setMatterCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  }, [docKey, bulkCopyActive, selectedKeys, rawTree, docName, matterId]);

  const handleSaveToCollection = useCallback(
    async ({ collectionId, name, prepare }) => {
      const documentIds = collectionCandidates
        .map((item) => item.id)
        .filter(Boolean);
      if (!documentIds.length) return;
      setSavingCollection(true);
      try {
        let saved;
        if (collectionId) {
          saved = await addCollectionDocuments(collectionId, documentIds);
          if (prepare) await prepareCollection(collectionId);
        } else {
          saved = await createCollection({
            matterId,
            name,
            documentIds,
            prepare,
          });
        }
        const missing = saved?.missing_document_ids?.length || 0;
        message[missing ? "warning" : "success"](
          missing
            ? `Saved to "${name || "collection"}" — ${missing} document(s) could not be found.`
            : `Saved ${documentIds.length} document${
                documentIds.length === 1 ? "" : "s"
              } to "${name || "collection"}".`
        );
        setCollectionModalOpen(false);
        setCollectionCandidates([]);
      } finally {
        setSavingCollection(false);
      }
    },
    [collectionCandidates, matterId]
  );

  const refreshTreeAndList = useCallback(() => {
    if (!matterId) return;
    dispatch(fetchMatterDetail(matterId));
    dispatch(
      fetchMatterDetailList({
        id: matterId,
        page: listState.currentPage || 1,
        sort_direction: listState.sort,
        sort_by: listState.sortBy,
      })
    );
  }, [dispatch, matterId, listState]);

  const handlePasteHere = useCallback(() => {
    if (pasting || !clipboardItems.length) return;
    if (!matterId) {
      message.error("Matter could not be resolved for paste.");
      return;
    }
    const targetFolderId = record.folder_id ?? record.folder ?? null;
    setPasting(true);
    copyDocumentsApi({
      documentIds: clipboardItems.map((item) => item.id),
      targetMatterId: matterId,
      targetFolderId,
    })
      .then((response) => {
        const payload = response?.data || {};
        const note = formatPasteNote({
          copied: payload.copied || 0,
          failed: payload.failed || 0,
          results: payload.results || [],
          destinationName: parentFolderName || "this matter",
        });
        if ((payload.failed || 0) === 0) message.success(note);
        else message.error(note);
        refreshTreeAndList();
      })
      .catch((err) => {
        message.error(extractCopyApiError(err, "Paste failed"));
      })
      .finally(() => setPasting(false));
  }, [
    pasting,
    clipboardItems,
    matterId,
    record.folder_id,
    record.folder,
    parentFolderName,
    refreshTreeAndList,
  ]);

  // Mirrors the "..." menu's Delete flow (same CustomModal copy, same
  // deleteDoc thunk moving the file to the Recycle Bin) — extended to bulk
  // delete for a multi-selection, same way Copy/Combine already scale up.
  const handleDeleteConfirm = useCallback(async () => {
    if (!docKey || deleting) return;
    setDeleting(true);
    try {
      if (bulkCopyActive) {
        const docKeys = selectedKeys.filter((key) => String(key).startsWith("D"));
        let deleteItems = collectDocumentsByUniqueIds(rawTree, docKeys);
        if (deleteItems.length < 2) {
          deleteItems = [{ id: docKey, name: docName }];
        }
        const ids = deleteItems.map((item) => item.id);
        const response = await bulkDeleteMatterDocuments(matterId, ids);
        const data = response?.data || {};
        const deletedCount = (data.deleted || []).length || ids.length;
        const errors = data.errors || [];
        if (errors.length > 0) {
          message.warning(
            `Moved ${deletedCount} file${deletedCount === 1 ? "" : "s"} to the Recycle Bin — ${errors.length} failed.`
          );
        } else {
          message.success(
            `Moved ${deletedCount} file${deletedCount === 1 ? "" : "s"} to the Recycle Bin.`
          );
        }
      } else {
        await dispatch(deleteDoc({ docId: docKey })).unwrap();
        message.success(`Moved "${docName || "document"}" to the Recycle Bin.`);
      }
      setDeleteConfirmOpen(false);
      refreshTreeAndList();
    } catch (err) {
      message.error(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Couldn't delete — try again."
      );
    } finally {
      setDeleting(false);
    }
  }, [
    docKey,
    deleting,
    bulkCopyActive,
    selectedKeys,
    rawTree,
    docName,
    matterId,
    dispatch,
    refreshTreeAndList,
  ]);

  const handleApplyPoa = useCallback(async () => {
    if (!docKey || !poaName || applyingPoa) return;
    setApplyingPoa(true);
    try {
      await applyPoaSignature(docKey, poaName);
      message.success(`POA signature applied for ${poaName}.`);
      setPoaModalOpen(false);
      setPoaName("");
      refreshTreeAndList();
    } catch (err) {
      message.error(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Couldn't apply the POA signature — try again."
      );
    } finally {
      setApplyingPoa(false);
    }
  }, [docKey, poaName, applyingPoa, refreshTreeAndList]);

  const items = useMemo(
    () => [
      {
        key: "copy-document",
        label: bulkCopyActive ? `Copy ${selectionDocCount} files` : "Copy",
        icon: <Copy size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          handleCopy();
        },
      },
      {
        key: "paste-documents",
        label: pasteMenuLabel(clipboardItems.length),
        icon: <ClipboardText size={ICON_SIZE} weight="regular" />,
        disabled: pasting || clipboardItems.length === 0,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          handlePasteHere();
        },
      },
      {
        key: "combine-files",
        label: bulkCopyActive ? `Combine ${selectionDocCount} files` : "Combine files",
        icon: <Stack size={ICON_SIZE} weight="regular" />,
        disabled: !docKey || !bulkCopyActive,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          handleCombine();
        },
      },
      {
        key: "add-to-collection",
        label: bulkCopyActive
          ? `Add ${selectionDocCount} files to collection`
          : "Add to collection",
        icon: <SquaresFour size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          handleOpenAddToCollection();
        },
      },
      {
        key: "rename-document",
        label: "Rename",
        icon: <PencilSimple size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          openRename();
        },
      },
      {
        key: "move-to-folder",
        label: "Move to folder",
        icon: <ArrowBendUpRight size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setMoveModalOpen(true);
        },
      },
      {
        key: "recategorize",
        label: "Categorize",
        icon: <FolderOpen size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          openModal("recategorize");
        },
      },
      goldStandardItem,
      isWordDocument && {
        key: "add-to-brief-bank",
        label: "Add to Brief Bank",
        icon: <Books size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setBriefBankModalOpen(true);
        },
      },
      {
        key: "send-fax",
        label: "Send fax",
        icon: <Printer size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setFaxModalOpen(true);
        },
      },
      fileType === "pdf" && canPlaceAttorneySignature && {
        key: "add-attorney-signature",
        label: "Add attorney signature",
        icon: <PenNib size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setAttorneySignatureOpen(true);
        },
      },
      fileType === "pdf" && {
        key: "apply-poa-signature",
        label: "Apply POA signature",
        icon: <PenNib size={ICON_SIZE} weight="regular" />,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setPoaModalOpen(true);
        },
      },
      { key: "divider-delete", type: "divider" },
      {
        key: "delete-document",
        label: (
          <span style={{ color: DANGER_FG }}>
            {bulkCopyActive ? `Delete ${selectionDocCount} files` : "Delete"}
          </span>
        ),
        icon: <Trash size={ICON_SIZE} weight="regular" color={DANGER_FG} />,
        danger: true,
        disabled: !docKey,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          setDeleteConfirmOpen(true);
        },
      },
    ].filter(Boolean),
    [
      docKey,
      openModal,
      goldStandardItem,
      handleCopy,
      handlePasteHere,
      handleCombine,
      handleOpenAddToCollection,
      openRename,
      isWordDocument,
      fileType,
      canPlaceAttorneySignature,
      bulkCopyActive,
      selectionDocCount,
      clipboardItems.length,
      pasting,
    ]
  );

  const handleHostContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={["contextMenu"]}
        overlayStyle={{ minWidth: 240 }}
      >
        <span
          data-testid="document-context-menu-host"
          data-tree-context-menu-host=""
          onContextMenu={handleHostContextMenu}
          style={{ display: "inline-block", width: "100%" }}
        >
          {children}
        </span>
      </Dropdown>

      <Modal
        open={removeConfirmOpen}
        title="Remove from Gold Standard Library"
        okText="Remove"
        okButtonProps={{ danger: true, loading: removing }}
        cancelButtonProps={{ disabled: removing }}
        onOk={handleRemoveConfirm}
        onCancel={() => setRemoveConfirmOpen(false)}
        destroyOnClose
      >
        <p>
          Remove <strong>{docName || "this document"}</strong> from the Gold
          Standard Library? The document itself will not be deleted.
        </p>
      </Modal>

      <CategorizeDocumentModal
        open={modalOpen}
        onClose={closeModal}
        mode={modalMode}
        documentId={docKey}
        documentName={docName}
        inferredCategory={inferredCategory}
        existingGoldStandard={goldStandard}
        matterId={matterId}
      />

      <MoveDocumentModal
        open={moveModalOpen}
        onClose={() => setMoveModalOpen(false)}
        documentId={docKey}
        documentName={docName}
        currentFolderId={record.folder_id ?? record.folder ?? null}
        matterId={matterId}
        documentRecord={record}
      />

      <SendFaxModal
        open={faxModalOpen}
        onClose={() => setFaxModalOpen(false)}
        documentId={docKey}
        documentName={docName}
        matterId={matterId ? String(matterId) : ""}
        matterName={rawTree?.display_name || ""}
      />

      <Modal
        title="Rename document"
        open={renameOpen}
        onCancel={() => !renaming && setRenameOpen(false)}
        onOk={handleRenameSubmit}
        okText={renaming ? "Renaming…" : "Rename"}
        cancelText="Cancel"
        okButtonProps={{ disabled: renaming || !pendingName?.trim() }}
        cancelButtonProps={{ disabled: renaming }}
        destroyOnClose
        maskClosable={!renaming}
      >
        <div style={{ marginBottom: 8 }}>
          Rename <strong>{docName}</strong>. Keep the file extension so the
          document opens correctly.
        </div>
        <Input
          autoFocus
          value={pendingName}
          onChange={(e) => setPendingName(e.target.value)}
          onPressEnter={handleRenameSubmit}
          placeholder="Document name"
          maxLength={255}
          aria-label="Document name"
          disabled={renaming}
        />
      </Modal>
      <Modal
        title="Apply POA signature"
        open={poaModalOpen}
        onCancel={() => !applyingPoa && setPoaModalOpen(false)}
        onOk={handleApplyPoa}
        okText={applyingPoa ? "Applying…" : "Apply signature"}
        cancelText="Cancel"
        okButtonProps={{ disabled: applyingPoa || !poaName }}
        cancelButtonProps={{ disabled: applyingPoa }}
        destroyOnClose
        maskClosable={!applyingPoa}
      >
        <div style={{ marginBottom: 8 }}>
          Stamp the appointed POA&apos;s signature onto{" "}
          <strong>{docName || "this document"}</strong>. Attorneys only — the
          signature is applied at the POA signature spots configured for the
          template.
        </div>
        <PoaSignatureSelect
          matterId={matterId}
          value={poaName}
          onChange={setPoaName}
          disabled={applyingPoa}
          variant="apply"
        />
      </Modal>
      <AttorneySignatureModal
        open={attorneySignatureOpen}
        document={docKey ? { id: docKey, name: docName } : null}
        onCancel={() => setAttorneySignatureOpen(false)}
        onSigned={() => {
          setAttorneySignatureOpen(false);
          refreshTreeAndList();
        }}
      />
      <AddToBriefBankModal
        open={briefBankModalOpen}
        documentId={docKey}
        documentName={docName}
        onClose={() => setBriefBankModalOpen(false)}
        onAdded={() => {
          setBriefBankModalOpen(false);
        }}
      />

      <SaveAsCollectionModal
        open={collectionModalOpen}
        documents={collectionCandidates}
        collections={matterCollections}
        loadingCollections={loadingCollections}
        saving={savingCollection}
        onCancel={() => !savingCollection && setCollectionModalOpen(false)}
        onSave={handleSaveToCollection}
      />

      <CustomModal
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        heading={bulkCopyActive ? `Delete ${selectionDocCount} files` : "Delete File"}
        content={
          bulkCopyActive
            ? `Are you sure you want to delete these ${selectionDocCount} files? They will be moved to the Recycle Bin and can be restored later.`
            : `Are you sure you want to delete "${docName || "this document"}"? The file will be moved to the Recycle Bin and can be restored later.`
        }
        btnTxtPrimary={
          deleting
            ? "Deleting…"
            : bulkCopyActive
              ? `Delete ${selectionDocCount} files`
              : "Delete file"
        }
        btnTxtSecondary="Cancel"
        onPrimaryClick={handleDeleteConfirm}
        onSecondaryClick={() => !deleting && setDeleteConfirmOpen(false)}
        disabled={deleting}
        variant="delete"
      />
    </>
  );
};

DocumentContextMenu.propTypes = {
  documentNode: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.node,
    record: PropTypes.object,
  }),
  parentFolderName: PropTypes.string,
  onCombineRequest: PropTypes.func,
  children: PropTypes.node,
};

export default DocumentContextMenu;
